import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaExchangeAlt, FaLock, FaLockOpen, FaSave } from 'react-icons/fa';
import { U3141GraphLotDialogHelpPopover } from './U3141GraphLotDialogHelpPopover';
import U2000CheckBox from '../../../../../u2000Core/components/formControls/checkbox/U2000CheckBox';
import U2000BasicDialog from '../../../../../u2000Core/components/dialog/U2000BasicDialog';
import U2000YesNoDialog from '../../../../../u2000Core/components/dialog/U2000YesNoDialog';
import U2000ActionBtn from '../../../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000AutoCompleteInput from '../../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput';
import U2000Label from '../../../../../u2000Core/components/formControls/label/U2000Label';
import u2000FormState, {
  U2000FormStateEnumAction,
  U2000FormStateEnumEditMode,
  type U2000FormState,
} from '../../../../../u2000Core/reducer/formReducer/U2000FormState';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000AuthContext } from '../../../../../u2000Core/context/auth/U2000UseAuthContext';
import { NS, U3141ROLES } from '../../../../U3141';
import {
  U3141EmptyChildLotChain,
  type U3141ChildLotChain,
  type U3141ResolveLotDetailForGraphAddBody,
  type U3141ResolveLotDetailForGraphAddResult,
} from '../../../../U3141Service';
import { U2000FormStateEnumState } from '../../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import U3141ChildLotChainLotFields from '../../u3141ChildLotChain/U3141ChildLotChainLotFields';
import { validateConcordanceEntries } from '../../u3141ChildLotChain/U3141ConcordanceUtils';
import { useU3141ResolveLotDetailForGraphAddHandlers } from './U3141UseResolveLotDetailForGraphAddHandlers';
import { useU3141CreateChildLotChainFromGraphHandlers } from './U3141UseCreateChildLotChainFromGraphHandlers';
import { u3141MapResolveGraphAddResultToChild } from '../../../../tools/u3141GraphAddResolveMapper';
import {
  u3141CloneChildLotChainSnapshot,
  u3141IsGraphAddFormDirty,
} from '../../../../tools/u3141GraphAddFormDirty';
import { u3141HydrateGraphAddLotDetailDistrictCadastre } from '../../../../tools/u3141GraphAddHydrateLotDetail';
import { useU3141ResolveConcordanceHandlers } from '../../u3141ChildLotChain/U3141UseFindDistrictCadastreHandlers';
import { useToast } from '../../../../../u2000Core/context/toast/U2000ToastContext';
import { useU3142FindSearchLotsHandlers } from '../../../../../u3142Lot/components/list/U3142UseFindSearchHandlers';
import type { U3142FindSearchLotItem } from '../../../../../u3142Lot/U3142Service';
import { u3142CompactLotForSearch } from '../../../../../u3142Lot/tools/u3142CompactLotForSearch';
import { u3142IsPreRenovationLotNumber } from '../../../../../u3142Lot/tools/u3142IsPreRenovationLotNumber';
import {
  u3141FormatGraphAddLotAutocompleteLotCellFromSearchItem,
  u3141FormatLotGraphDialogLotNumber,
} from '../../../../tools/u3141FormatLotGraphLabel';
import {
  u3141GraphLotDefaultFieldsLocked,
  u3141GraphLotLockCanApply,
  u3141GraphLotLockCloseDisabled,
} from '../../../../tools/u3141GraphLotDialogLock';

const U3141_GRAPH_ADD_LOT_SEARCH_FIELDS = ['lot', 'propertyDistrict', 'cadastre', 'rankLabel'] as const;

function u3141FormatGraphAddLotSearchCell(item: U3142FindSearchLotItem, field: string): string {
  if (field === 'lot') {
    return u3141FormatGraphAddLotAutocompleteLotCellFromSearchItem(item);
  }
  if (!u3142IsPreRenovationLotNumber(item.lot)) {
    return '';
  }
  const value = item[field as keyof U3142FindSearchLotItem];
  return typeof value === 'string' ? value : '';
}

function u3141GraphAddLotSearchNeedsCadastralColumns(results: U3142FindSearchLotItem[]): boolean {
  return results.some((row) => u3142IsPreRenovationLotNumber(row.lot));
}

/** BL-0697 — seules les concordances mal formées bloquent l’enregistrement graphe. */
function validateAddLotForm(child: U3141ChildLotChain, t: (key: string, opts?: { ns: string }) => string): Record<string, string> {
  const result: Record<string, string> = {};
  const concordanceValidation = validateConcordanceEntries(child?.lotDetail?.concordance ?? []);
  if (!concordanceValidation.valid) {
    result['lotDetail.concordance'] = t('errConcordanceInvalid', { ns: NS });
  }
  return result;
}

type U3141GraphAddLotResolveMeta = {
  found: boolean;
  lotState?: 'validate' | 'notValidate';
};

type U3141GraphAddLotDraftRow = {
  lotPick: { lot: string; id?: string } | null;
};

const emptyLotDraft = (): U3141GraphAddLotDraftRow => ({ lotPick: null });

function U3141GraphAddDialogSection({
  title,
  children,
  className = '',
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}>
      <div className="border-b border-gray-100 bg-muted/40 px-4 py-2.5">
        <h3 className="text-sm font-semibold tracking-tight text-gray-900">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export interface U3141AddLotFromGraphDialogProps {
  isOpen: boolean;
  onClose: () => void;
  masterLotChainId: string;
  nodePosition: { x: number; y: number };
  existingChildLotChains: U3141ChildLotChain[];
  applyLotChainGraphSnapshotFromGraphCreate: (graph: U3141ChildLotChain[]) => void;
  refetchMasterLotChain?: () => Promise<void>;
  reloadLotChainGraphIfDialogOpen?: (masterLotChainId: string) => Promise<void>;
  onChildCreated: (child: U3141ChildLotChain) => void;
  overlayClassName?: string;
}

export const U3141AddLotFromGraphDialog: React.FC<U3141AddLotFromGraphDialogProps> = ({
  isOpen,
  onClose,
  masterLotChainId,
  nodePosition,
  existingChildLotChains,
  applyLotChainGraphSnapshotFromGraphCreate,
  refetchMasterLotChain,
  reloadLotChainGraphIfDialogOpen,
  onChildCreated,
  overlayClassName = 'z-110',
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const canEditLot = hasRole(U3141ROLES.EDIT);
  const findSearchLotsHandlers = useU3142FindSearchLotsHandlers();
  const { resolveDistrict, resolveCadastre } = useU3141ResolveConcordanceHandlers();

  const [lotCommitted, setLotCommitted] = useState(false);
  const [committedLotNumber, setCommittedLotNumber] = useState('');
  const [lastResolve, setLastResolve] = useState<U3141GraphAddLotResolveMeta | null>(null);
  const [resolveSnapshot, setResolveSnapshot] = useState<U3141ChildLotChain | null>(null);
  const [fieldsLockedByUser, setFieldsLockedByUser] = useState<boolean | null>(null);
  const [formData, setFormData] = useState<U3141ChildLotChain>(() => ({ ...U3141EmptyChildLotChain }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editStepHint, setEditStepHint] = useState<string | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const [lotDraftFormState, lotDraftFormDispatch] = useReducer<U2000FormState, unknown>(u2000FormState, u2000IntFormState);
  const lotDraftBusinessHandlers = useMemo(
    () => ({
      newRecord: () => emptyLotDraft(),
      validateForm: () => ({}),
      saveForme: async () => {},
    }),
    [],
  );
  const lotDraftFormHandlers = useU2000FormStateHandlers<U3141GraphAddLotDraftRow>({
    formState: lotDraftFormState,
    formStateDispatch: lotDraftFormDispatch,
    businessHandlers: lotDraftBusinessHandlers,
  });

  const resolvingFromAutocompleteRef = useRef(false);
  const lotSearchInputRef = useRef<HTMLInputElement>(null);
  /** Valeur capturée avant blur autocomplete (clic Valider). */
  const lotValidateSnapshotRef = useRef('');

  const resetState = useCallback(() => {
    setLotCommitted(false);
    setCommittedLotNumber('');
    setLastResolve(null);
    setResolveSnapshot(null);
    setFieldsLockedByUser(null);
    setFormData({ ...U3141EmptyChildLotChain });
    setErrors({});
    setEditStepHint(null);
    setIsValidating(false);
    setIsSaving(false);
    setDiscardConfirmOpen(false);
    lotValidateSnapshotRef.current = '';
    lotDraftFormDispatch({
      type: U2000FormStateEnumAction.SETEDITMODE,
      data: { mode: U2000FormStateEnumEditMode.ADD },
    });
    lotDraftFormDispatch({
      type: U2000FormStateEnumAction.SETDATA,
      data: { row: emptyLotDraft() },
    });
  }, [lotDraftFormDispatch]);

  useEffect(() => {
    if (!isOpen) resetState();
  }, [isOpen, resetState]);

  const addDialogLotState = formData.lotDetail?.lotState ?? lastResolve?.lotState;
  const graphLockAutoApply = u3141GraphLotLockCanApply(addDialogLotState);
  const fieldsLocked =
    fieldsLockedByUser ?? u3141GraphLotDefaultFieldsLocked(addDialogLotState, formData);
  const businessFieldsEditable = !fieldsLocked;

  const isDirty = useMemo(
    () => u3141IsGraphAddFormDirty(formData, resolveSnapshot),
    [formData, resolveSnapshot],
  );

  const formStateHandlers = useMemo((): U2000FormStateHandlers<U3141ChildLotChain> => {
    const noop = () => {};
    return {
      getData: () => formData,
      setData: setFormData,
      getFormState: () => U2000FormStateEnumState.EDIT,
      getErrors: () => errors,
      setErrors,
      setEditMode: noop,
      saveData: noop,
      getOldData: () => null,
      afterSaveData: noop,
      cancelData: noop,
      getFormEditMode: () => 0,
      getTouched: () => ({}),
      markTouched: noop,
      setOtherErrors: noop,
      getOtherErrors: () => ({}),
      setSubmitted: noop,
      isSubmitted: () => false,
      isValid: () => Object.keys(errors).length === 0,
      getCustomAttributes: () => ({}),
      setCustomAttributes: noop,
      onRecordChange: noop,
      onPageChange: noop,
      onLeave: noop,
      runAfterFormCommitNavigation: (_nav: () => void) => {},
    };
  }, [formData, errors]);

  const { resolveLotForGraphAdd } = useU3141ResolveLotDetailForGraphAddHandlers({ t, toast });
  const { createFromGraph } = useU3141CreateChildLotChainFromGraphHandlers({
    t,
    toast,
    onChildCreated,
    applyLotChainGraphSnapshotFromGraphCreate,
    refetchMasterLotChain,
    reloadLotChainGraphIfDialogOpen,
  });

  const applyResolveToForm = useCallback(
    async (resolve: U3141ResolveLotDetailForGraphAddResult, trimmed: string) => {
      const meta: U3141GraphAddLotResolveMeta = {
        found: resolve.found,
        lotState: resolve.lotDetail?.lotState === 'validate' ? 'validate' : resolve.found ? 'notValidate' : undefined,
      };
      setLastResolve(meta);
      setFieldsLockedByUser(null);
      setCommittedLotNumber(trimmed);
      setLotCommitted(true);

      if (!resolve.found) {
        const draft: U3141ChildLotChain = {
          ...U3141EmptyChildLotChain,
          lotDetail: {
            ...U3141EmptyChildLotChain.lotDetail,
            lot: trimmed,
            lotState: 'notValidate',
            concordance: [],
            concordanceOrigines: [],
          },
        };
        setFormData(draft);
        setResolveSnapshot(u3141CloneChildLotChainSnapshot(draft));
        setEditStepHint(t('msgAddLotFromGraphManualEntry', { ns: NS }));
        return;
      }

      let childDraft = u3141MapResolveGraphAddResultToChild(resolve, trimmed);
      if (resolve.lotDetail) {
        childDraft = await u3141HydrateGraphAddLotDetailDistrictCadastre(childDraft, resolve.lotDetail, {
          resolveDistrict,
          resolveCadastre,
        });
      }
      setFormData(childDraft);
      setResolveSnapshot(u3141CloneChildLotChainSnapshot(childDraft));

      if (meta.lotState === 'validate') {
        setEditStepHint(t('msgAddLotFromGraphExistingValidated', { ns: NS }));
      } else {
        setEditStepHint(t('msgAddLotFromGraphEditBeforeSave', { ns: NS }));
      }
    },
    [resolveCadastre, resolveDistrict, t],
  );

  const runResolve = useCallback(
    async (resolveBody: U3141ResolveLotDetailForGraphAddBody) => {
      const trimmed = resolveBody.lot.trim();
      if (!trimmed) {
        toast.error(t('errAddLotFromGraphLotRequired', { ns: NS }), t('errorTitle', { ns: NS }));
        return;
      }
      const compact = u3142CompactLotForSearch(trimmed);
      const duplicate = existingChildLotChains.some((c) => {
        const existing = (c.lotDetail?.lot ?? '').trim();
        if (!existing) return false;
        return u3142CompactLotForSearch(existing).toLowerCase() === compact.toLowerCase();
      });
      if (duplicate) {
        toast.error(t('errChildLotAlreadyInChain', { ns: NS }), t('errorTitle', { ns: NS }));
        return;
      }

      setIsValidating(true);
      try {
        const resolve = await resolveLotForGraphAdd(masterLotChainId, {
          ...resolveBody,
          lot: trimmed,
        });
        if (!resolve) {
          return;
        }
        if (resolve.alreadyInMasterChain) {
          return;
        }
        await applyResolveToForm(resolve, trimmed);
      } finally {
        setIsValidating(false);
      }
    },
    [applyResolveToForm, existingChildLotChains, masterLotChainId, resolveLotForGraphAdd, t, toast],
  );

  /**
   * Numéro pour POST resolve : ligne autocomplete avec id, sinon saisie du champ (BL-0661 saisie libre).
   * Pas de pré-contrôle U3142 au Valider — le backend décide found true/false.
   */
  const getCommittedLotForResolve = useCallback((): U3141ResolveLotDetailForGraphAddBody | null => {
    const pick = lotDraftFormHandlers.getData()?.lotPick;
    const pickedLot = pick?.lot?.trim();
    const pickedId = pick?.id?.trim();
    if (pickedLot && pickedId) {
      lotValidateSnapshotRef.current = '';
      return { lot: pickedLot, lotDetailId: pickedId };
    }

    const snap = lotValidateSnapshotRef.current.trim();
    if (snap) {
      lotValidateSnapshotRef.current = '';
      return { lot: snap };
    }

    const typed = lotSearchInputRef.current?.value.trim() ?? pickedLot ?? '';
    if (!typed) {
      toast.error(t('errAddLotFromGraphLotRequired', { ns: NS }), t('errorTitle', { ns: NS }));
      return null;
    }
    return { lot: typed };
  }, [lotDraftFormHandlers, t, toast]);

  const captureLotValidateSnapshot = useCallback(() => {
    const fromInput = lotSearchInputRef.current?.value.trim() ?? '';
    if (fromInput) {
      lotValidateSnapshotRef.current = fromInput;
    }
  }, []);

  const handleValidateLotMouseDown = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      captureLotValidateSnapshot();
    },
    [captureLotValidateSnapshot],
  );

  const handleValidateLot = useCallback(() => {
    const resolveBody = getCommittedLotForResolve();
    if (!resolveBody) return;
    void runResolve(resolveBody);
  }, [getCommittedLotForResolve, runResolve]);

  useEffect(() => {
    const el = lotSearchInputRef.current;
    if (!el || !isOpen || lotCommitted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (el.getAttribute('aria-expanded') === 'true') {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      lotValidateSnapshotRef.current = el.value.trim();
      handleValidateLot();
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [handleValidateLot, isOpen, lotCommitted]);

  const onSearchLots = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      const result = await findSearchLotsHandlers.search(criterias);
      return {
        ...result,
        results: result.results.map((r: U3142FindSearchLotItem) => ({ value: r.lot, ...r })),
      };
    },
    [findSearchLotsHandlers],
  );

  const extractLotPick = useCallback(
    (item: U3142FindSearchLotItem | null): { fieldName: string; value: unknown }[] => {
      const lot = item?.lot?.trim() ?? '';
      if (lot && item?.id && !resolvingFromAutocompleteRef.current) {
        resolvingFromAutocompleteRef.current = true;
        void runResolve({ lot, lotDetailId: item.id }).finally(() => {
          resolvingFromAutocompleteRef.current = false;
        });
      }
      return [{ fieldName: 'lotPick', value: item ? { lot: item.lot ?? '', id: item.id } : null }];
    },
    [runResolve],
  );

  const resetLotDraftPhase = useCallback(() => {
    setLotCommitted(false);
    setCommittedLotNumber('');
    setLastResolve(null);
    setResolveSnapshot(null);
    setFieldsLockedByUser(null);
    setFormData({ ...U3141EmptyChildLotChain });
    setErrors({});
    setEditStepHint(null);
    lotDraftFormHandlers.setData(emptyLotDraft());
  }, [lotDraftFormHandlers]);

  const handleChangeLotRequest = () => {
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    resetLotDraftPhase();
  };

  const confirmDiscardAndChangeLot = () => {
    setDiscardConfirmOpen(false);
    resetLotDraftPhase();
  };

  const toggleFieldsLock = () => {
    if (!canEditLot) return;
    if (u3141GraphLotLockCloseDisabled(fieldsLocked, formData?.lotDetail)) return;
    setFieldsLockedByUser(!fieldsLocked);
  };

  const lockCloseDisabled = u3141GraphLotLockCloseDisabled(fieldsLocked, formData?.lotDetail);

  const lockTooltip = !canEditLot
    ? t('ttlAddLotFromGraphLockNoEdit', { ns: NS })
    : lockCloseDisabled
      ? t('ttlAddLotFromGraphLockRequiredFieldsForClose', { ns: NS })
      : graphLockAutoApply
      ? fieldsLocked
        ? t('ttlAddLotFromGraphLockClosed', { ns: NS })
        : t('ttlAddLotFromGraphLockOpen', { ns: NS })
      : fieldsLocked
        ? t('ttlAddLotFromGraphLockClosed', { ns: NS })
        : t('ttlAddLotFromGraphLockManualOnly', { ns: NS });

  const lockTooltipWithContext = useMemo(() => {
    if (editStepHint && fieldsLocked) {
      return `${lockTooltip}\n\n${editStepHint}`;
    }
    return lockTooltip;
  }, [editStepHint, fieldsLocked, lockTooltip]);

  const lotStatusBadge = useMemo(() => {
    if (!lotCommitted || !lastResolve) return null;
    if (!lastResolve.found) {
      return (
        <span className="inline-flex h-5 max-h-5 items-center rounded-md border border-amber-300/80 bg-amber-50 px-2 py-0 text-xs font-semibold leading-none text-amber-950">
          {t('lblAddLotFromGraphNewLot', { ns: NS })}
        </span>
      );
    }
    const isValidated = lastResolve.lotState === 'validate';
    return (
      <span
        className={`inline-flex h-5 max-h-5 items-center rounded-md border px-2 py-0 text-xs font-semibold leading-none ${
          isValidated
            ? 'border-emerald-300/80 bg-emerald-50 text-emerald-950'
            : 'border-sky-300/80 bg-sky-50 text-sky-950'
        }`}
      >
        {t('lblAddLotFromGraphExistingLot', { ns: NS })}
        {isValidated ? ` · ${t('lblAddLotFromGraphValidated', { ns: NS })}` : ''}
      </span>
    );
  }, [lastResolve, lotCommitted, t]);

  const graphAddLotInlineEnd = useMemo(
    () => (
      <div className="flex max-w-full flex-wrap items-center justify-end gap-2 sm:gap-3">
        <U2000CheckBox
          formStateHandlers={formStateHandlers}
          fieldName="lotDetail.partieDeLot"
          label={t('lblLotDetail.partieDeLot', { ns: NS })}
          readonly={!businessFieldsEditable}
        />
        <U2000ActionBtn
          variant="secondary"
          icon={fieldsLocked ? FaLock : FaLockOpen}
          label={t('lblAddLotFromGraphLockToggle', { ns: NS })}
          handleClick={toggleFieldsLock}
          disabled={!canEditLot || isValidating || isSaving || lockCloseDisabled}
          title={lockTooltipWithContext}
        />
        <U2000ActionBtn
          variant="secondary"
          icon={FaExchangeAlt}
          label={t('btnAddLotFromGraphChangeLot', { ns: NS })}
          handleClick={handleChangeLotRequest}
          disabled={isValidating || isSaving}
        />
      </div>
    ),
    [
      businessFieldsEditable,
      canEditLot,
      fieldsLocked,
      formStateHandlers,
      handleChangeLotRequest,
      isSaving,
      isValidating,
      lockCloseDisabled,
      lockTooltipWithContext,
      t,
      toggleFieldsLock,
    ],
  );

  const handleSave = async () => {
    const validationErrors = validateAddLotForm(formData, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    const selectedConcordanceEntries = [...(formData.lotDetail?.concordance ?? [])];
    setIsSaving(true);
    try {
      const saved = await createFromGraph({
        masterLotChainId,
        childLotChain: formData,
        selectedConcordanceEntries,
        nodePosition,
      });
      if (saved) {
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const nestedOverlay = overlayClassName === 'z-110' ? 'z-120' : 'z-130';

  return (
    <>
      <U2000BasicDialog
        isOpen={isOpen}
        onClose={onClose}
        title={t('dlgAddLotFromGraphTitle', { ns: NS })}
        width="w-[min(920px,92vw)]"
        height="max-h-[90vh]"
        overlayClassName={overlayClassName}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-1 pb-4 pt-0">
          {!lotCommitted ? (
            <U3141GraphAddDialogSection title={t('lblAddLotFromGraphSectionLot', { ns: NS })}>
              <div className={`relative ${isValidating ? 'pointer-events-none opacity-70' : ''}`}>
                <div className="space-y-1.5">
                  <U2000Label label={t('lblAddLotFromGraphLot', { ns: NS })} htmlFor="lotPick" />
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3">
                    <div className="min-w-0 flex-1">
                      <U2000AutoCompleteInput
                      pageSize={100}
                      formStateHandlers={lotDraftFormHandlers as U2000FormStateHandlers<U3141GraphAddLotDraftRow>}
                      fieldName="lotPick"
                      mainFieldName="lot"
                      onSearch={onSearchLots}
                      displayFn={(item) => {
                        const row = item as U3142FindSearchLotItem | null;
                        return u3141FormatLotGraphDialogLotNumber(row?.lot);
                      }}
                      overrideExtractDataFn={(item) => extractLotPick(item as U3142FindSearchLotItem | null)}
                      basicResultHeight={280}
                      dropdownWidth={640}
                      resultLabels={[
                        t('lblAddLotFromGraphLot', { ns: NS }),
                        t('lblAddLotConcordanceDistrict', { ns: NS }),
                        t('lblAddLotConcordanceCadastre', { ns: NS }),
                        t('lblAddLotConcordanceRank', { ns: NS }),
                      ]}
                      resultFields={[...U3141_GRAPH_ADD_LOT_SEARCH_FIELDS]}
                      formatResultCell={(item, field) =>
                        u3141FormatGraphAddLotSearchCell(item as U3142FindSearchLotItem, field)
                      }
                      resolveShowResultHeader={(results) =>
                        u3141GraphAddLotSearchNeedsCadastralColumns(results as U3142FindSearchLotItem[])
                      }
                      minCarLaunchSearch={1}
                      readonly={isValidating}
                      inputRef={lotSearchInputRef}
                    />
                    </div>
                    <U2000ActionBtn
                      className="box-border h-[46px] w-full shrink-0 py-0 sm:w-auto"
                      icon={FaCheck}
                      label={isValidating ? t('msgAddLotFromGraphValidating', { ns: NS }) : t('btnAddLotFromGraphValidate', { ns: NS })}
                      onMouseDown={handleValidateLotMouseDown}
                      handleClick={() => void handleValidateLot()}
                      disabled={isValidating}
                    />
                  </div>
                </div>
              </div>
            </U3141GraphAddDialogSection>
          ) : null}

          {lotCommitted ? (
            <>
              <U3141GraphAddDialogSection title={t('lblAddLotFromGraphSectionDetails', { ns: NS })}>
                <U3141ChildLotChainLotFields
                  formStateHandlers={formStateHandlers}
                  mode="graphAdd"
                  businessFieldsEditable={businessFieldsEditable}
                  graphAddLotLabelAddon={lotStatusBadge}
                  graphAddLotInlineEnd={graphAddLotInlineEnd}
                />
              </U3141GraphAddDialogSection>
            </>
          ) : null}
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-muted/30 px-3 py-3">
            <U3141GraphLotDialogHelpPopover
              key={lotCommitted ? 'add-lot-help-details' : 'add-lot-help-identify'}
              mode="add"
              addDetailsStepActive={lotCommitted}
              t={t}
              contextualHint={lotCommitted ? editStepHint : null}
              disabled={isSaving || isValidating}
            />
            <div className="flex flex-wrap justify-end gap-2">
              <U2000ActionBtn
                variant="secondary"
                label={t('btnAddLotFromGraphCancel', { ns: NS })}
                handleClick={onClose}
                disabled={isSaving || isValidating}
              />
              <U2000ActionBtn
                icon={FaSave}
                label={isSaving ? t('msgAddLotFromGraphSaving', { ns: NS }) : t('btnAddLotFromGraphSave', { ns: NS })}
                handleClick={() => void handleSave()}
                disabled={isSaving || isValidating}
              />
            </div>
          </div>
        </div>
      </U2000BasicDialog>

      <U2000YesNoDialog
        isOpen={discardConfirmOpen}
        onClose={(result) => {
          if (result === 'yes') confirmDiscardAndChangeLot();
          else setDiscardConfirmOpen(false);
        }}
        title={t('dlgAddLotFromGraphDiscardTitle', { ns: NS })}
        width="max-w-md"
        overlayClassName={nestedOverlay}
      >
        <p className="max-w-sm text-sm leading-relaxed text-gray-700">{t('dlgAddLotFromGraphDiscardMessage', { ns: NS })}</p>
      </U2000YesNoDialog>
    </>
  );
};
